export const CONSENT_COOKIE = "cookie_consent";
export const CONSENT_MAX_AGE_DAYS = 365;

/** GTM custom event fired after an in-app grant, so tags that missed Consent Initialization can run */
export const GTM_CONSENT_GRANTED_EVENT = "cookie_consent_granted";

/** Window event used by the footer link to reopen the banner */
export const CONSENT_CHANGE_EVENT = "maura:consent-change";

export const CONSENT_STATUSES = {
  GRANTED: "granted",
  DENIED: "denied",
};

export const CONSENT_SIGNALS = {
  AD_STORAGE: "ad_storage",
  AD_USER_DATA: "ad_user_data",
  AD_PERSONALIZATION: "ad_personalization",
  ANALYTICS_STORAGE: "analytics_storage",
  PERSONALIZATION_STORAGE: "personalization_storage",
};

const GATED_SIGNALS = Object.values(CONSENT_SIGNALS);

function buildConsentState(status) {
  return Object.fromEntries(GATED_SIGNALS.map((signal) => [signal, status]));
}

const DEFAULT_CONSENT_STATE = {
  ...buildConsentState(CONSENT_STATUSES.DENIED),
  security_storage: CONSENT_STATUSES.GRANTED,
  wait_for_update: 500,
};

/**
 * Inline script that establishes Consent Mode v2 defaults before GTM loads, then immediately
 * applies a returning visitor's stored choice. Setting the default here rather than inside GTM
 * avoids a Consent Initialization tag overwriting that stored choice.
 */
export function getConsentBootstrapScript() {
  return `
window.dataLayer = window.dataLayer || [];
window.gtag = window.gtag || function () { window.dataLayer.push(arguments); };
window.gtag("consent", "default", ${JSON.stringify(DEFAULT_CONSENT_STATE)});
window.gtag("set", "ads_data_redaction", true);
window.gtag("set", "url_passthrough", true);
window.__mauraConsentBootstrapped = true;
(function () {
  var match = document.cookie.match(/(?:^|; )${CONSENT_COOKIE}=([^;]*)/);
  var stored = match ? decodeURIComponent(match[1]) : null;
  if (stored === "${CONSENT_STATUSES.GRANTED}") {
    window.gtag("consent", "update", ${JSON.stringify(buildConsentState(CONSENT_STATUSES.GRANTED))});
  } else if (stored === "${CONSENT_STATUSES.DENIED}") {
    window.gtag("consent", "update", ${JSON.stringify(buildConsentState(CONSENT_STATUSES.DENIED))});
  }
})();
`.trim();
}

/** Returns "granted", "denied", or null when the visitor has not chosen yet. */
export function getStoredConsent() {
  if (typeof document === "undefined") return null;

  const match = document.cookie.match(new RegExp(`(?:^|; )${CONSENT_COOKIE}=([^;]*)`));
  const value = match ? decodeURIComponent(match[1]) : null;

  return value === CONSENT_STATUSES.GRANTED || value === CONSENT_STATUSES.DENIED ? value : null;
}

export function setStoredConsent(status) {
  if (typeof document === "undefined") return;

  const expires = new Date(Date.now() + CONSENT_MAX_AGE_DAYS * 24 * 60 * 60 * 1000).toUTCString();
  const secure = window.location.protocol === "https:" ? "; Secure" : "";

  // biome-ignore lint/suspicious/noDocumentCookie: the bootstrap reads this synchronously before GTM loads, which the async Cookie Store API cannot do
  document.cookie = `${CONSENT_COOKIE}=${status}; expires=${expires}; path=/; SameSite=Lax${secure}`;
}

/** Applies the visitor's choice to Consent Mode, notifies GTM, and persists it. */
export function updateGtagConsent(status) {
  if (typeof window === "undefined") return;

  if (typeof window.gtag === "function") {
    window.gtag("consent", "update", buildConsentState(status));
  }

  if (status === CONSENT_STATUSES.GRANTED) {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ event: GTM_CONSENT_GRANTED_EVENT });
  }

  setStoredConsent(status);
}

export function openConsentPreferences() {
  if (typeof window === "undefined") return;

  window.dispatchEvent(new CustomEvent(CONSENT_CHANGE_EVENT));
}
